import { useCallback } from "react";
import { VoiceBotOptions, ProjectInterface } from "../types";
import { applyPrompt, applyGreeting } from "../api";
import { AudioChat } from "../AudioChat";
import { ErrorHandler } from "../errorHandler";

interface UseConfigUpdaterProps {
    selectedProject: ProjectInterface;
    voiceChat: AudioChat | null;
    setOptions: (options: Partial<VoiceBotOptions>) => void;
    errorHandler: ErrorHandler;
    options: VoiceBotOptions;
}

export const useConfigUpdater = ({
    selectedProject,
    voiceChat,
    setOptions,
    errorHandler,
    options,
}: UseConfigUpdaterProps) => {
    const handleApplyPrompt = useCallback(async () => {
        if (selectedProject.isRealtime) {
            if (voiceChat) {
                voiceChat.updateRealTimeConfig();
            }
        } else {
            await applyPrompt(selectedProject.versionEndpoint, options.lang, options.instructions).catch((error) =>
                errorHandler.handle(error)
            );
        }
    }, [selectedProject, voiceChat, options, errorHandler]);

    const handleApplyGreeting = useCallback(
        async (greeting: string) => {
            if (selectedProject.isRealtime) {
                if (voiceChat) {
                    setOptions({ greeting });
                    voiceChat.updateRealTimeConfig();
                }
            } else {
                setOptions({ greeting });
                await applyGreeting(selectedProject.versionEndpoint, options.lang, greeting).catch((error) =>
                    errorHandler.handle(error)
                );
            }
        },
        [selectedProject, voiceChat, setOptions, options.lang, errorHandler]
    );

    const handleApplyTtsInstructions = useCallback(
        async (ttsInstructions: string) => {
            setOptions({ ttsInstructions });

            if (selectedProject.isRealtime) {
                if (voiceChat) {
                    voiceChat.updateRealTimeConfig();
                }
            }
        },
        [selectedProject, voiceChat, setOptions]
    );

    return {
        handleApplyPrompt,
        handleApplyGreeting,
        handleApplyTtsInstructions,
    };
};
